import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import { ClipboardList, ChevronDown, ChevronUp, Loader2, Car, User, Calendar, ExternalLink } from "lucide-react";

/**
 * Compact panel that shows the previous work orders in which a material was ordered.
 * Useful for Auftragsartikel: when the user picks a previously-used one, they can see
 * who ordered it and when — perfect for cross-referencing with a supplier catalog.
 *
 * Props:
 *   materialId: the material id to load history for (required)
 *   materialName: label to show in the header
 *   currentOrderId: optional; excludes the current order from the list (during edit)
 *   isOrderSpecific: bool; only affects header wording
 *   defaultOpen: bool; expand history by default
 */
const MaterialUsageHistory = ({ materialId, materialName, currentOrderId = null, isOrderSpecific = false, defaultOpen = false }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(defaultOpen);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!materialId) {
      setHistory([]);
      setLoaded(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.get(`/api/materials/${materialId}/usage-history?limit=10`)
      .then(res => {
        if (cancelled) return;
        const items = res.data || [];
        const filtered = currentOrderId ? items.filter(i => i.order_id !== currentOrderId) : items;
        setHistory(filtered);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      })
      .finally(() => {
        if (!cancelled) { setLoading(false); setLoaded(true); }
      });
    return () => { cancelled = true; };
  }, [materialId, currentOrderId]);

  if (!materialId) return null;
  if (loaded && history.length === 0) return null; // no history — nothing to show

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return iso; }
  };

  return (
    <div
      className={`mt-2 rounded-md border ${isOrderSpecific ? "border-amber-200 bg-amber-50/50" : "border-slate-200 bg-slate-50/50"}`}
      data-testid="material-usage-history"
    >
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-black/5 rounded-md"
        data-testid="material-usage-history-toggle"
      >
        <div className="flex items-center gap-2 text-xs">
          <ClipboardList className={`w-3.5 h-3.5 ${isOrderSpecific ? "text-amber-700" : "text-slate-600"}`} />
          <span className="font-medium">
            {loading ? "Bestellhistorie wird geladen…"
              : `${history.length}× zuvor bestellt${materialName ? ` (${materialName})` : ""}`}
          </span>
        </div>
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
        ) : expanded ? (
          <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
        )}
      </button>
      {expanded && !loading && (
        <div className="px-3 pb-2 space-y-1.5">
          {history.map((h, idx) => (
            <button
              key={h.order_id}
              type="button"
              onClick={(e) => { e.stopPropagation(); navigate(`/auftraege?order=${h.order_id}`); }}
              className="w-full text-xs bg-white border border-slate-200 rounded p-2 space-y-1 text-left hover:border-primary hover:shadow-sm transition-all group"
              data-testid={`material-usage-history-item-${idx}`}
              title="Auftrag öffnen"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium flex items-center gap-1.5">
                  {h.auftragsnummer ? `#${h.auftragsnummer}` : `Auftrag ${h.order_id.slice(0, 8)}`}
                  <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity" />
                </span>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${
                  h.status === "erledigt" ? "bg-green-100 text-green-700"
                    : h.status === "in_bearbeitung" ? "bg-blue-100 text-blue-700"
                    : "bg-slate-100 text-slate-700"
                }`}>
                  {h.status === "erledigt" ? "Erledigt" : h.status === "in_bearbeitung" ? "In Bearbeitung" : "Offen"}
                </span>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(h.created_at)}</span>
                <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{h.customer_name}</span>
                <span className="inline-flex items-center gap-1"><Car className="w-3 h-3" />{h.vehicle_info}</span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span>Menge: <strong>{h.anzahl}</strong></span>
                {h.einzelpreis > 0 && (
                  <span>Einzelpreis: <strong>{h.einzelpreis.toFixed(2)} €</strong></span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MaterialUsageHistory;
